import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Modal, Icon, TextField, Typography } from "@ann/web-components";
import { useDebounce } from "../../../../../hooks";
import {
  ModalBodyInner,
  SearchRow,
  SearchInputWrap,
  NewChatRow,
  ListScroll,
  Section,
  SectionTitle,
  ConversationItem,
  ConversationItemIcon,
  ConversationItemLabel,
  ClearSearchButton,
  StyledModal,
} from "./ConversationHistoryModal.styled";

const MODAL_WIDTH = "700px";
const MODAL_HEIGHT = "400px";

const MS_DAY = 24 * 60 * 60 * 1000;

/** Mismo día en la zona horaria local del usuario */
const isToday = (date) => {
  if (!date) return false;
  const d = new Date(date);
  const today = new Date();
  return (
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear()
  );
};

/** Día anterior en la zona horaria local del usuario */
const isYesterday = (date) => {
  if (!date) return false;
  const d = new Date(date);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    d.getDate() === yesterday.getDate() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getFullYear() === yesterday.getFullYear()
  );
};

/** Últimos 7 días pero excluyendo hoy y ayer (ya agrupados arriba) */
const isWithinLast7Days = (date) => {
  if (!date) return false;
  const ts = new Date(date).getTime();
  const now = Date.now();
  if (ts >= now || ts >= now - MS_DAY) return false; // no futuro ni “hoy” ni “ayer”
  return ts >= now - 7 * MS_DAY && ts < now;
};

const isWithinLast30Days = (date) => {
  if (!date) return false;
  const ts = new Date(date).getTime();
  const now = Date.now();
  return ts >= now - 30 * MS_DAY && ts < now - 7 * MS_DAY;
};

/** Más de 30 días (se muestra en "Last 3 months") */
const isOlderThan30Days = (date) => {
  if (!date) return false;
  const ts = new Date(date).getTime();
  return ts < Date.now() - 30 * MS_DAY;
};

const getConversationId = (c) =>
  c.conversationUuid ?? c.conversation_uuid ?? c.id ?? c.uuid;

const getConversationDate = (c) =>
  c.lastUpdate ?? c.lastUpdated ?? c.last_updated ?? c.updatedAt ?? c.startedAt ?? c.createdAt ?? c.created_at ?? null;

/**
 * Modal tipo "Search chats": búsqueda, "New chat" y lista de conversaciones
 * agrupadas por Today, Yesterday, Last 7 days, Last 30 days, Last 3 months.
 *
 * @param {boolean} isOpen - Si el modal está abierto
 * @param {Function} onClose - Callback al cerrar
 * @param {Array} conversations - Lista de conversaciones { id/conversationUuid, title, lastUpdated/createdAt, ... }
 * @param {Function} onSelectConversation - (conversationId) => void al elegir una conversación
 * @param {Function} onNewChat - () => void al elegir "New chat"
 */
const ConversationHistoryModal = ({
  isOpen,
  onClose,
  conversations = [],
  onSelectConversation,
  onNewChat,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce(searchValue, 400);

  useEffect(() => {
    if (!isOpen) setSearchValue("");
  }, [isOpen]);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const filteredConversations = useMemo(() => {
    if (!debouncedSearch.trim()) return conversations;
    const lower = debouncedSearch.trim().toLowerCase();
    return conversations.filter((c) => {
      const title = (c.title ?? c.name ?? "").toLowerCase();
      return title.includes(lower);
    });
  }, [conversations, debouncedSearch]);

  const { today, yesterday, last7Days, last30Days, last3Months } = useMemo(() => {
    const todayList = [];
    const yesterdayList = [];
    const last7DaysList = [];
    const last30DaysList = [];
    const last3MonthsList = [];
    const sortByDate = (a, b) => new Date(getConversationDate(b)) - new Date(getConversationDate(a));

    filteredConversations.forEach((c) => {
      const date = getConversationDate(c);
      if (isToday(date)) {
        todayList.push(c);
      } else if (isYesterday(date)) {
        yesterdayList.push(c);
      } else if (isWithinLast7Days(date)) {
        last7DaysList.push(c);
      } else if (isWithinLast30Days(date)) {
        last30DaysList.push(c);
      } else if (isOlderThan30Days(date)) {
        last3MonthsList.push(c);
      } else {
        // Sin fecha o fecha no clasificable: mostrar en Last 7 days
        last7DaysList.push(c);
      }
    });

    todayList.sort(sortByDate);
    yesterdayList.sort(sortByDate);
    last7DaysList.sort(sortByDate);
    last30DaysList.sort(sortByDate);
    last3MonthsList.sort(sortByDate);

    return {
      today: todayList,
      yesterday: yesterdayList,
      last7Days: last7DaysList,
      last30Days: last30DaysList,
      last3Months: last3MonthsList,
    };
  }, [filteredConversations]);

  const handleSelect = useCallback(
    (c) => {
      const id = getConversationId(c);
      onSelectConversation?.(id);
      onClose?.();
    },
    [onSelectConversation, onClose]
  );

  const handleNewChatClick = useCallback(() => {
    onNewChat?.();
    onClose?.();
  }, [onNewChat, onClose]);

  const renderItem = (c) => {
    const id = getConversationId(c);
    const title = c.title ?? c.name ?? "Conversation name";
    return (
      <ConversationItem
        key={id}
        role="button"
        tabIndex={0}
        onClick={() => handleSelect(c)}
        onKeyDown={(e) => e.key === "Enter" && handleSelect(c)}
      >
        <ConversationItemIcon>
          <Icon name="chat_bubble" size="small" color="neutral" />
        </ConversationItemIcon>
        <ConversationItemLabel title={title}>{title}</ConversationItemLabel>
      </ConversationItem>
    );
  };

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={onClose}
      closeFromOutside
      size="medium"
      width={MODAL_WIDTH}
      className="conversation-history-modal"
    >
      <Modal.Body>
        <ModalBodyInner style={{ height: MODAL_HEIGHT }} onClick={(e) => e.stopPropagation()}>
          <SearchRow>
            <SearchInputWrap id="search-input-wrap">
              <TextField
                iconLeft="search"
                iconRight=""
                label=""
                size="small"
                placeholder="Search chats..."
                value={searchValue}
                onChange={(e) => {
                  const v = typeof e === "object" && e?.target != null ? e.target.value : e;
                  setSearchValue(v ?? "");
                }}
              />
              <ClearSearchButton type="button" onClick={handleClose} aria-label="Close modal">
                <Icon name="close" size="medium" color="neutral" />
              </ClearSearchButton>
            </SearchInputWrap>
          </SearchRow>

          <NewChatRow
            onClick={handleNewChatClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleNewChatClick()}
          >
            <Icon color="neutral" name="edit_square" size="small" />
            <Typography category="body" size="medium" weight="regular" color="body">
              New Chat
            </Typography>
          </NewChatRow>

          <ListScroll>
            {today.length > 0 && (
              <Section>
                <SectionTitle>Today</SectionTitle>
                {today.map(renderItem)}
              </Section>
            )}
            {yesterday.length > 0 && (
              <Section>
                <SectionTitle>Yesterday</SectionTitle>
                {yesterday.map(renderItem)}
              </Section>
            )}
            {last7Days.length > 0 && (
              <Section>
                <SectionTitle>Last 7 days</SectionTitle>
                {last7Days.map(renderItem)}
              </Section>
            )}
            {last30Days.length > 0 && (
              <Section>
                <SectionTitle>Last 30 days</SectionTitle>
                {last30Days.map(renderItem)}
              </Section>
            )}
            {last3Months.length > 0 && (
              <Section>
                <SectionTitle>Last 3 months</SectionTitle>
                {last3Months.map(renderItem)}
              </Section>
            )}
            {today.length === 0 &&
              yesterday.length === 0 &&
              last7Days.length === 0 &&
              last30Days.length === 0 &&
              last3Months.length === 0 && (
                <Typography category="body" size="medium" weight="regular" color="body">
                  No conversations found.
                </Typography>
              )}
          </ListScroll>
        </ModalBodyInner>
      </Modal.Body>
    </StyledModal>
  );
};

export default ConversationHistoryModal;
